<template>
  <div ref="editorRef">
    <content-row class="!mt-1">
      <TCheckBox
        v-model="repeat"
        :label="$t('forms.repeat')"
        data-testid="gradient-repeat"
      />

      <TCheckBox
        v-model="radial"
        :label="$t('forms.radial')"
        data-testid="gradient-radial"
      />
    </content-row>
    <!-- Gradient Bar -->
    <div class="mt-3">
      <div
        ref="gradientBarRef"
        class="gradient-bar relative h-8 cursor-crosshair rounded-md border border-solid border-gray-600"
        :style="{ background: gradientBackground }"
        @click="addColorStop"
      >
        <!-- Color Stops -->
        <div
          v-for="(stop, index) in colorStops"
          :key="`stop-${index}`"
          class="group !absolute top-[-3px] z-10 cursor-pointer"
          :style="{ left: stop.position + '%', transform: 'translateX(-50%)' }"
          data-testid="color-stop"
          @mousedown.stop="startDragStop(index, $event)"
          @click.stop="toggleColorPicker(index)"
        >
          <div
            class="relative h-9 w-2 rounded-sm border border-solid border-gray-300 bg-gray-900 transition-colors duration-200 hover:border-gray-200"
            :class="{ '!border-accent-1': activeStopIndex === index }"
            :style="{ backgroundColor: stop.color }"
          ></div>
          <!-- Remove button -->
          <button
            v-if="colorStops.length > 2"
            class="absolute right-[-5px] top-[-8px] grid h-[13px] w-[13px] cursor-pointer items-center justify-center rounded-full bg-destructive-1 leading-[1] text-gray-10 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
            @click.stop="removeStop(index)"
          >
            <Close class="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>

    <!-- Controls -->
    <content-row v-if="!radial" class="!block">
      <TLabel>{{ $t("forms.angle") }}</TLabel>
      <TSlider
        v-model="angle"
        :label="$t('forms.angle')"
        data-testid="gradient-angle"
        :min="0"
        :max="360"
        unit="deg"
        xxs
        @drag-start="generalStore.colorSelecting = true"
        @drag-end="generalStore.colorSelecting = false"
      />
    </content-row>

    <!-- Color Picker Popup -->
    <Teleport to="body">
      <div
        v-if="showColorPicker && activeStop"
        class="fixed bottom-0 left-0 right-0 top-0 z-[99999] bg-black/50"
        data-testid="color-picker-popover"
        @click="closeColorPicker"
      >
        <div class="absolute" :style="pickerSytle" @click.stop>
          <colorPickerBox
            :default-color="activeStop.color"
            @color-change="updateActiveColor($event.hex)"
          />
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
  import { parseGradient } from "@topol/utils";
  import colorPickerBox from "@/forms/elements/colorPickerBox.vue";
  import { useGeneralStore } from "@/stores/generalStore";
  import { Close } from "@topol/icons";
  import { ref, computed, onMounted, onUnmounted, useTemplateRef } from "vue";
  import { TCheckBox, TLabel, TSliderClassic as TSlider } from "@topol/twig-ui";

  interface ColorStop {
    color: string;
    position: number;
  }

  // Props & Model
  const model = defineModel<string>();

  // Refs
  const gradientBarRef = useTemplateRef("gradientBarRef");

  const generalStore = useGeneralStore();

  const activeStopIndex = ref(0);
  const wasMoving = ref(false);
  const activeStopColor = ref<string>();
  const showColorPicker = ref(false);
  const pickerPosition = ref({ x: 0, y: 0 });
  const isDragging = ref(false);
  const dragType = ref<"stop" | "sb" | "hue" | "alpha" | null>(null);
  const pickerSytle = ref({
    left: pickerPosition.value.x + "px",
    top: pickerPosition.value.y + "px",
    width: 198 + "px",
  });
  const mousemoveTimeout = ref<ReturnType<typeof setTimeout> | null>(null);

  // Computed
  const parsed = computed(() => {
    return parseGradient(model.value);
  });

  const colorStops = computed({
    get() {
      if (!parsed.value || !parsed.value?.colorStops) {
        return [];
      }
      return parsed.value.colorStops;
    },
    set() {},
  });

  const options = computed({
    get() {
      if (!parsed.value) {
        return {
          repeat: false,
          radial: false,
          direction: "90deg",
        };
      }
      return {
        repeat: parsed.value.type.startsWith("repeating"),
        radial: parsed.value.type.includes("radial"),
        direction: parsed.value.direction,
      };
    },
    set() {},
  });

  const angle = computed({
    get() {
      if (options.value.radial) {
        return 90;
      }
      return parseInt(options.value.direction || "90");
    },
    set(val: number) {
      options.value.direction = val + "deg";
      updateGradient();
    },
  });

  const radial = computed({
    get() {
      return options.value.radial;
    },
    set(val: boolean) {
      options.value.radial = val;
      if (val) {
        options.value.direction = "circle";
      } else {
        options.value.direction = "90deg";
      }
      updateGradient();
    },
  });

  const repeat = computed({
    get() {
      return options.value.repeat;
    },
    set(val: boolean) {
      options.value.repeat = val;
      updateGradient();
    },
  });

  const activeStop = computed(() => {
    return colorStops.value[activeStopIndex.value];
  });

  const gradientBackground = computed(() => {
    const sortedStops = colorStops.value;
    const stopsStr = sortedStops
      .map((stop) => `${stop.color} ${stop.position}%`)
      .join(", ");
    return `linear-gradient(90deg, ${stopsStr})`;
  });

  function popupStyle() {
    setTimeout(() => {
      const colorPicker = document.querySelector(".hu-color-picker");
      const maxWidth = window.innerWidth - 40;
      const maxHeight = window.innerHeight - 40;
      const popupWidth = 198;
      //@ts-expect-error
      const popupHeight = colorPicker ? colorPicker.offsetHeight : 340;

      let x = pickerPosition.value.x - popupWidth / 2;
      let y = pickerPosition.value.y + 20;

      // Keep popup in viewport
      if (x < 20) x = 20;
      if (x + popupWidth > maxWidth) x = maxWidth - popupWidth;
      if (y + popupHeight > maxHeight)
        y = pickerPosition.value.y - popupHeight - 20;

      pickerSytle.value = {
        left: x + "px",
        top: y + "px",
        width: popupWidth + "px",
      };
    }, 10);
  }

  function hexToRgb(hex: string) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : { r: 0, g: 0, b: 0 };
  }

  function updateActiveColor(color: string) {
    if (!activeStop.value) return;

    activeStop.value.color = color;
    updateGradient();
  }

  // Color Picker Management
  function toggleColorPicker(index: number) {
    if (showColorPicker.value && activeStopIndex.value === index) {
      closeColorPicker();
      return;
    }

    if (wasMoving.value) {
      return;
    }

    activeStopIndex.value = index;

    // Calculate position
    if (gradientBarRef.value) {
      const rect = gradientBarRef.value.getBoundingClientRect();
      const stopPosition =
        (colorStops.value[index].position / 100) * rect.width;
      pickerPosition.value = {
        x: rect.left + stopPosition,
        y: rect.bottom,
      };
    }

    showColorPicker.value = true;
    popupStyle();
  }

  function closeColorPicker() {
    showColorPicker.value = false;
  }
  function interpolateColor(
    color1: string,
    color2: string,
    position: number
  ): string {
    const rgb1 = hexToRgb(color1);
    const rgb2 = hexToRgb(color2);

    const r = rgb1.r + (rgb2.r - rgb1.r) * position;
    const g = rgb1.g + (rgb2.g - rgb1.g) * position;
    const b = rgb1.b + (rgb2.b - rgb1.b) * position;

    return rgbToHex(r, g, b);
  }

  function rgbToHex(r: number, g: number, b: number): string {
    return `#${[r, g, b]
      .map((x) => {
        const hex = Math.round(x).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")}`;
  }

  // Event Handlers
  function addColorStop(event: MouseEvent) {
    if (isDragging.value) return;
    if (!gradientBarRef.value || showColorPicker.value) return;

    const rect = gradientBarRef.value.getBoundingClientRect();
    const position = ((event.clientX - rect.left) / rect.width) * 100;

    // Interpolate color at this position
    const sortedStops = [...colorStops.value].sort(
      (a, b) => a.position - b.position
    );

    let leftStop = sortedStops[0];
    let rightStop = sortedStops[sortedStops.length - 1];

    if (
      position <= sortedStops[0].position ||
      position >= sortedStops[sortedStops.length - 1].position
    ) {
      const newStop: ColorStop = {
        color:
          position <= sortedStops[0].position
            ? leftStop.color
            : rightStop.color,
        position: Math.round(position),
      };
      colorStops.value.push(newStop);
      updateGradient();
      return;
    }

    for (let i = 0; i < sortedStops.length - 1; i++) {
      if (
        position >= sortedStops[i].position &&
        position <= sortedStops[i + 1].position
      ) {
        leftStop = sortedStops[i];
        rightStop = sortedStops[i + 1];
        break;
      }
    }

    const newStop: ColorStop = {
      color: interpolateColor(
        leftStop.color,
        rightStop.color,
        (Math.round(position) - leftStop.position) /
          (rightStop.position - leftStop.position)
      ),
      position: Math.round(position),
    };

    colorStops.value.push(newStop);

    updateGradient();
  }

  function removeStop(index: number) {
    if (colorStops.value.length <= 2) return;

    colorStops.value.splice(index, 1);
    if (activeStopIndex.value >= colorStops.value.length) {
      activeStopIndex.value = colorStops.value.length - 1;
    }
    closeColorPicker();
    updateGradient();
  }

  function startDragStop(index: number, event: MouseEvent) {
    event.preventDefault();
    if (mousemoveTimeout.value) {
      clearTimeout(mousemoveTimeout.value);
      mousemoveTimeout.value = null;
    }
    generalStore.colorSelecting = true;
    activeStopIndex.value = index;
    activeStopColor.value = colorStops.value[index].color;
    isDragging.value = true;
    dragType.value = "stop";
    wasMoving.value = false;
    closeColorPicker(); // Close picker while dragging

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }

  function onMouseMove(event: MouseEvent) {
    if (!isDragging.value) return;
    wasMoving.value = true;
    updateStopPosition(event);
  }

  function onMouseUp(event: MouseEvent) {
    event.preventDefault();
    mousemoveTimeout.value = setTimeout(() => {
      isDragging.value = false;
      generalStore.colorSelecting = false;
    }, 50);
    dragType.value = null;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  }

  function updateStopPosition(event: MouseEvent) {
    if (!gradientBarRef.value || !activeStop.value) return;

    const rect = gradientBarRef.value.getBoundingClientRect();
    const position = Math.max(
      0,
      Math.min(100, ((event.clientX - rect.left) / rect.width) * 100)
    );

    activeStop.value.position = Math.round(position);
    updateGradient();
  }

  function updateGradient() {
    const actStop = colorStops.value[activeStopIndex.value];
    const sortedStops = [...colorStops.value].sort(
      (a, b) => a.position - b.position
    );
    activeStopIndex.value = sortedStops.findIndex((item) => item == actStop);
    const stopsStr = sortedStops
      .map((stop) => `${stop.color} ${stop.position}%`)
      .join(", ");

    const type = options.value.radial ? "radial-gradient" : "linear-gradient";
    const direction = options.value.direction
      ? `${options.value.direction}, `
      : "";
    const gradient = `${type}(${direction}${stopsStr})`;

    model.value = options.value.repeat ? `repeating-${gradient}` : gradient;
  }

  // Lifecycle
  onMounted(() => {
    updateGradient();
  });

  onUnmounted(() => {
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  });
</script>

<style scoped>
  .gradient-bar {
    background-image:
      linear-gradient(45deg, #333 25%, transparent 25%),
      linear-gradient(-45deg, #333 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #333 75%),
      linear-gradient(-45deg, transparent 75%, #333 75%);
    background-size: 8px 8px;
    background-position:
      0 0,
      0 4px,
      4px -4px,
      -4px 0px;
  }
  .gradient-bar,
  .gradient-bar * {
    box-sizing: border-box;
  }
</style>
